import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { Scrollbox } from 'pixi-scrollbox';
import { observer } from 'mobx-react-lite';
import { Container, Graphics, Sprite, Text } from '@pixi/react';
import { isMobile } from '../../../../core/helpers/isMobile';
import BoostedJackpotPopupDate from '../boostedJackpotPopupDate';
import BoostedJackpotInfoPopupWinCount from './boostedJackpotInfoPopupWinCount';
import BoostedJackpotInfoPopupPrizePool from './boostedJackpotInfoPopupPrizePool';
import BoostedJackpotInfoPopupRules from './boostedJackpotInfoPopupRules';
import { gameStore } from '../../../../store/gameStore';
import { boostedJackpotStore } from '../../../../store/boostedJackpotStore';
import { applicationStore } from '../../../../store/applicationStore';

let timeoutId;

const BoostedJackpotInfoPopup = observer(
  ({
    rulesIcons,
    boostedJackpotInfoPopup,
    boostedJackpotInfoPopupUI,
    boostedJackpotPopupCloseButton,
    containerConfigBoostedJackpotInfoPopup,
  }) => {
    const { setIsSpinStart } = gameStore;
    const { setIsShowBlackLayer } = applicationStore;
    const {
      winningCount,
      lastWinnerDate,
      prizePoolAmount,
      lastWinnerAmount,
      winningCountLeft,
      largestWinnerDate,
      largestWinnerAmount,
      prizePoolLeftAmount,
      isBoostedJackpotInfoPopup,
      setIsBoostedJackpotInfoPopup,
    } = boostedJackpotStore;

    const scrollBoxRef = useRef(null);
    const scrollBox = useMemo(
      () =>
        new Scrollbox({
          boxWidth: 455,
          boxHeight: 635,
          overflowY: 'scroll',
          overflowX: 'hidden',
          scrollbarSize: 10,
          scrollbarOffsetVertical: 5,
          scrollbarBackgroundAlpha: 0,
        }),

      [isBoostedJackpotInfoPopup]
    );

    useEffect(() => {
      if (scrollBox && scrollBoxRef.current && isBoostedJackpotInfoPopup) {
        const { children } = scrollBoxRef.current;
        children.forEach((child) => {
          timeoutId = setTimeout(() => {
            scrollBox.content.addChild(child);
            scrollBoxRef.current.visible = true;
          }, 0);
        });

        scrollBox.content.screenWidth = isMobile() ? 540 : 1000;
        scrollBox.content.screenHeight = 610;

        const handleScroll = () => {
          const { scrollbar } = scrollBox;
          scrollbar.interactive = false;
          scrollbar.clear();

          const scrollPercent =
            scrollBox.scrollTop / (scrollBox.content.height - scrollBox.options.boxHeight);
          const maxScroll = scrollBox.options.boxHeight - 150;
          const currentPosition = scrollPercent * maxScroll;

          scrollbar.beginFill(0xffffff, 0.5);
          scrollbar.drawRoundedRect(455, currentPosition + 60, 6, 60, 20); // Added offset and border
          scrollbar.endFill();
          scrollBox.content.x = 0;
        };
        setTimeout(() => {
          handleScroll();
        }, 0);
        scrollBox.content.addEventListener('moved', handleScroll);
        scrollBox.update();
        scrollBoxRef.current.addChild(scrollBox);
      }
      return () => {
        // scrollBox.content.removeEventListener('moved', handleScroll);
        clearTimeout(timeoutId);
        timeoutId = null;
      };
    }, [scrollBox, isBoostedJackpotInfoPopup]);

    const handlePointerUp = useCallback(() => {
      setIsSpinStart(false);
      setIsBoostedJackpotInfoPopup(false);
      setIsShowBlackLayer(false);
    }, [setIsBoostedJackpotInfoPopup, setIsSpinStart, setIsShowBlackLayer]);

    if (!isBoostedJackpotInfoPopup) {
      return null;
    }

    return (
      <>
        {!isMobile() && (
          <Graphics
            draw={(g) => {
              g.clear();
              g.alpha = 0.7;
              g.beginFill(0x030303);
              g.drawRect(0, 0, 1440, 860);
              g.interactive = true;
            }}
          />
        )}
        <Container name='BoostedJackpotInfoPopup' {...containerConfigBoostedJackpotInfoPopup}>
          <Sprite texture={boostedJackpotInfoPopup} anchor={0} />
          <Container ref={scrollBoxRef} x={17} visible={false}>
            <Text
              x={224}
              y={30}
              anchor={0.5}
              text={applicationStore.canvasTranslationKeys.BOOSTED_JACKPOT}
              style={{
                fontSize: 18,
                fontWeight: 600,
                fontFamily: 'poppinsMedium',
                fill: '#FFFFFF',
                align: 'center',
                wordWrap: true,
                wordWrapWidth: 206,
              }}
              resolution={2}
            />
            <Sprite texture={boostedJackpotInfoPopupUI} anchor={0} x={5} y={60} />
            <BoostedJackpotPopupDate
              x={100}
              y={349}
              marginInText={28}
              marginInContainer={73}
              hiderTextXPosition={15}
              durationTextPositions={{ x: 126, y: 30 }}
              stylesTimer={{
                _fontSize: 24,
                _fontWeight: 700,
                _fontFamily: 'poppinsMedium',
                _fill: '#FFFFFF',
                align: 'center',
              }}
            />
            <BoostedJackpotInfoPopupWinCount
              winningCount={winningCount}
              winningCountLeft={winningCountLeft}
            />
            <BoostedJackpotInfoPopupPrizePool
              lastWinnerDate={lastWinnerDate}
              prizePoolAmount={prizePoolAmount}
              lastWinnerAmount={lastWinnerAmount}
              largestWinnerDate={largestWinnerDate}
              largestWinnerAmount={largestWinnerAmount}
              prizePoolLeftAmount={prizePoolLeftAmount}
            />
            <BoostedJackpotInfoPopupRules rulesIcons={rulesIcons} />
          </Container>
        </Container>
        <Sprite
          x={isMobile() ? 470 : 920}
          y={isMobile() ? 211 : 100}
          anchor={0}
          interactive
          cursor='pointer'
          pointerup={handlePointerUp}
          texture={boostedJackpotPopupCloseButton}
        />
      </>
    );
  }
);

export default BoostedJackpotInfoPopup;
